import type { FilterModel } from '@ag-grid-community/core';
import { formatISO } from 'date-fns';

type Filter = Record<string, FilterModel>;
type Maybe<T> = T | null | undefined;

export function createQueryParamFromFilterModel(
    filterModel: Filter,
    searchParams = new URLSearchParams(),
    filterableColumnIds: string[] = [],
): URLSearchParams {
    // Remove all existing filter-related query params first
    const cleanedSearchParams = new URLSearchParams(searchParams);
    filterableColumnIds.forEach((colId) => {
        cleanedSearchParams.delete(colId);
        cleanedSearchParams.delete(`${colId}In`); // For set filters
    });

    if (!Object.keys(filterModel).length) {
        return cleanedSearchParams;
    }

    // Setup a map where the key will be the query parameter name and the value will be the query parameter value
    const initialState = new Map<string, string>();
    // Loop through all the keys in the filterModel and set the corresponding query parameter values
    // e.g. { colId: { filterType: 'text', filter: 'foo' } } => ?colId=foo
    const simplifiedModel = Object.entries(filterModel).reduce(
        (acc, [colId, model]) => {
            createQueryParamForModel({ colId, model, record: acc });
            return acc;
        },
        initialState,
    );
    // Convert the map to a URLSearchParams object
    return buildQuery(cleanedSearchParams, simplifiedModel);
}

interface CreateFilterModelOptions {
    colId: string;
    model: FilterModel;
    record: Map<string, string>;
}

/**
 * Converts a FilterModel to a query parameter
 */
function createQueryParamForModel({
    colId,
    model,
    record = new Map(),
}: CreateFilterModelOptions): Map<string, string | Map<string, string>> {
    // Handle the different types of filters
    switch (model.filterType) {
        case 'text':
        case 'number': {
            if (!model.filter) break;
            record.set(colId, createQueryParamValue(model.filter, model.type));
            break;
        }
        // TODO: Handle the "inRange" type, where we have both dateFrom and dateTo
        case 'date': {
            if (!model.dateFrom) break;
            // We only want the date part of the dateFrom, not the time
            const dateFrom = formatISO(new Date(model.dateFrom), {
                representation: 'date',
            });
            record.set(colId, createQueryParamValue(dateFrom, model.type));
            break;
        }
        case 'set': {
            const values = model.values.reduce<string[]>((acc, value) => {
                if (value) {
                    acc.push(value);
                }
                return acc;
            }, []);
            record.set(`${colId}In`, values.join(','));
            break;
        }

        default:
            console.warn(
                `filterType ${model.filterType} not supported for column ${colId}`,
            );
    }

    return record;
}

function createQueryParamValue<T extends Maybe<string | number>>(
    value: T,
    operator: Maybe<string>,
): string {
    switch (typeof value) {
        case 'string':
            return operator ? `${operator}:${value}` : value;
        case 'number':
            return operator
                ? `${operator}:${value.toString()}`
                : value.toString();
        default:
            return '';
    }
}

/**
 * Build a URLSearchParams object from a Map
 */
function buildQuery(
    searchParams: URLSearchParams,
    data: Map<string, string>,
): URLSearchParams {
    data.forEach((value, key) => {
        searchParams.set(key, value);
    });
    return searchParams;
}
